import { useCallback, useEffect, useRef, useState } from 'react';
import { useMaintenance } from './useMaintenance';
import { endMaintenance } from '../services/maintenanceService';
import { runBauphaseFlushers } from '../services/bauphaseFlush';
import type { MaintenancePhase, MaintenanceState } from '../services/maintenanceLogic';

/**
 * Die Bauphase aus Sicht der App-Huelle: was passiert, wenn sie ZU geht, und
 * was, wenn sie wieder AUF geht.
 *
 * useMaintenance liefert nur den Zustand; dieser Hook haengt die Folgen daran,
 * damit App.tsx nichts weiter tun muss, als ihn aufzurufen und drei Dinge zu
 * rendern (Leiste, Wartungsseite, Torwaechter-Flag). Die eigentlichen
 * Handgriffe — Warteschlange leeren, Projekt schliessen, Version pruefen —
 * kommen als Callbacks herein; sie leben in App.tsx, weil dort die Refs und
 * Setter dafuer stehen.
 *
 * Ablauf beim Uebergang nach 'locked' (genau einmal je Bauphase):
 *   1. `flushAllNow()` — Debounce abbrechen, aktuelles Projekt und alles
 *      Uebrige hochladen, Journal-Spiegel nachziehen. So lange darf der
 *      Torwaechter der Warteschlange NICHT sperren, sonst wuerde er genau
 *      diesen Flush abweisen (`uploadsBlocked` wird erst danach wahr).
 *   2. bis SAVE_WAIT_MS auf `isSaved()` warten (250-ms-Takt).
 *   3. das offene Projekt schliessen — auch wenn nicht alles raus ist: der
 *      Server nimmt ohnehin nichts mehr an, und der Eintrag liegt sicher in
 *      der IndexedDB. Dann steht `queuedHint` auf der Wartungsseite.
 *
 * Uebergang 'locked' → 'none': Version pruefen, dann neu laden. Der Reload ist
 * der Sinn der Uebung — nach der Bauphase soll jeder Client mit dem neuen
 * Build weiterarbeiten, und die Warteschlange geht beim Login raus.
 */

export interface BauphaseGateArgs {
    /** Aus ohne Sitzung — dann weder Abo noch Folgen. */
    enabled: boolean;
    role?: string | null;
    currentProjectId: string | null;
    /** Debounce abbrechen + flushProject(current) + flushAll + retryJournalMirrors. */
    flushAllNow: () => Promise<void>;
    /** saveStatusRef.current in ('saved', 'idle'). */
    isSaved: () => boolean;
    /** Schliesspfad wie exitProjectForIdle, ohne Leerlauf-Hinweis. */
    closeProject: (reason: 'maintenance') => void;
    /** versionGateRef.current?.checkNow() */
    checkVersionNow: () => Promise<void>;
    /**
     * `__BUILD_TIME__` dieses Bundles. Damit meldet useMaintenance „Update ist
     * da", sobald dieser Build neuer ist als der beim Ankuendigen ausgelieferte
     * — und die Bauphase endet von selbst (Nr. 96). Derselbe Wert wird gegen
     * den Mindest-Build des Build-Riegels geprueft (Nr. 112/113).
     */
    buildTime?: string | null;
    /**
     * Dieses Bundle liegt unter dem Mindest-Build — der Server wuerde jeden
     * Schreibvorgang mit `BUILD_VERALTET` abweisen.
     *
     * Wird durchgereicht, nicht behandelt: die Entscheidung Reload oder
     * Update-Balken faellt in App.tsx, mit derselben Regel wie beim
     * Version-Gate (decideOnOutdated).
     */
    onBuildTooOld?: () => void;
}

export interface BauphaseGate {
    state: MaintenanceState;
    /** phase === 'locked' — unabhaengig davon, ob der Admin schon drin ist. */
    locked: boolean;
    /** Vollbild-Wartungsseite zeigen. */
    showScreen: boolean;
    /** Hinweis auf der Wartungsseite, wenn nach SAVE_WAIT_MS noch etwas auf dem Geraet liegt. */
    queuedHint: string | null;
    /** Der Admin hat „Umgehen" gedrueckt. */
    adminEntered: boolean;
    /**
     * Torwaechter-Flag fuer canUploadQueueEntry: zu UND der Flush von Schritt 1
     * ist durch UND kein Admin. Bewusst nicht gleich `locked` — siehe Ablauf.
     */
    uploadsBlocked: boolean;
    enterAsAdmin: () => void;
    endNow: () => Promise<void>;
}

/** Wie lange nach dem Flush auf „gesichert" gewartet wird, bevor das Projekt trotzdem zugeht. */
export const SAVE_WAIT_MS = 20_000;

const SAVE_POLL_MS = 250;

export const QUEUED_HINT = 'Ihre letzten Eingaben liegen auf diesem Gerät und gehen nach der Bauphase raus.';

/**
 * Wie oft die Wartungsseite nach einem neuen Build fragt.
 *
 * Das Gate selbst fetcht hoechstens alle 5 min (minIntervalMs); der Aufruf
 * hier ist nur der Anstoss, den ein Tab im Vordergrund sonst nicht bekommt
 * (focus/visibilitychange bleiben aus, und der Herzschlag in App.tsx laeuft
 * ebenfalls im 5-min-Takt — zusammen also spaetestens 5 min nach dem Deploy).
 * Sieht das Gate den neuen Build, laedt App.tsx neu (Editor ist zu, nichts
 * offen); der frische Build meldet dann ueber useMaintenance „Update ist da".
 */
const LOCKED_VERSION_POLL_MS = 60_000;

const sleep = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

export const useBauphaseGate = (args: BauphaseGateArgs): BauphaseGate => {
    const { enabled, role } = args;

    // Die Callbacks im Ref: App.tsx darf sie als schlichte Closures
    // hereinreichen, ohne dass der Effekt unten bei jedem Render neu anlaeuft.
    const argsRef = useRef(args);
    argsRef.current = args;

    const { state, refresh } = useMaintenance({
        enabled,
        role,
        buildTime: args.buildTime,
        onBuildTooOld: args.onBuildTooOld,
    });

    const [adminEntered, setAdminEntered] = useState(false);
    const [queuedHint, setQueuedHint] = useState<string | null>(null);
    const [flushDone, setFlushDone] = useState(false);

    /** Vorherige Phase — fuer den Uebergang 'locked' → 'none'. */
    const prevPhaseRef = useRef<MaintenancePhase>('none');
    /** Der Sperr-Ablauf laeuft genau einmal je Bauphase. */
    const lockHandledRef = useRef(false);
    /** Nach dem Abbau darf ein noch laufender Ablauf keinen Zustand setzen. */
    const aliveRef = useRef(false);

    useEffect(() => {
        aliveRef.current = true;
        return () => { aliveRef.current = false; };
    }, []);

    const reset = useCallback(() => {
        lockHandledRef.current = false;
        setAdminEntered(false);
        setQueuedHint(null);
        setFlushDone(false);
    }, []);

    useEffect(() => {
        if (!enabled) {
            prevPhaseRef.current = 'none';
            reset();
            return;
        }

        const prev = prevPhaseRef.current;
        prevPhaseRef.current = state.phase;

        if (state.phase === 'locked' && !lockHandledRef.current) {
            lockHandledRef.current = true;
            void (async () => {
                // 0. Erst die angemeldeten Sicherungen (bauphaseFlush): Eingaben,
                //    die nur im React-State eines anderen Baums liegen — die
                //    Freelancer-Begehung —, erreicht flushAllNow nicht. Schluckt
                //    Fehler selbst (allSettled), haengt also nie.
                await runBauphaseFlushers();
                try {
                    await argsRef.current.flushAllNow();
                } catch (e) {
                    // Ein gescheiterter Flush ist kein Grund, das Zusperren
                    // auszulassen — der Eintrag bleibt in der Warteschlange.
                    console.warn('[bauphase] Sichern vor der Sperre fehlgeschlagen:', e);
                }
                if (!aliveRef.current) return;
                setFlushDone(true);

                const deadline = Date.now() + SAVE_WAIT_MS;
                while (aliveRef.current && !argsRef.current.isSaved() && Date.now() < deadline) {
                    await sleep(SAVE_POLL_MS);
                }
                if (!aliveRef.current) return;
                if (!argsRef.current.isSaved()) setQueuedHint(QUEUED_HINT);
                if (argsRef.current.currentProjectId) argsRef.current.closeProject('maintenance');
            })();
            return;
        }

        if (prev === 'locked' && state.phase === 'none') {
            // Erst die Version pruefen: sieht das Gate das neue Deployment,
            // landet der Reload ohnehin im frischen Build; sieht es keines,
            // schadet der Reload nicht — die Warteschlange geht beim Login raus.
            void (async () => {
                try {
                    await argsRef.current.checkVersionNow();
                } catch (e) {
                    console.warn('[bauphase] Versionspruefung vor dem Neuladen fehlgeschlagen:', e);
                }
                window.location.reload();
            })();
            return;
        }

        // Keine Sperre mehr (Ende, oder der Admin hat waehrend der Sperre neu
        // angekuendigt): alles zuruecksetzen, damit die naechste Sperre wieder
        // von vorn laeuft. NICHT bei 'locked' — dort kaeme der Ablauf sonst
        // bei jedem Effektlauf erneut.
        if (state.phase !== 'locked') reset();
    }, [enabled, state.phase, reset]);

    // Waehrend der Sperre auf das Deploy lauern — siehe LOCKED_VERSION_POLL_MS.
    // Nicht fuer den Admin, der „Umgehen" gedrueckt hat: er arbeitet, und ein
    // Reload mitten in seiner Arbeit ist genau das, was das Gate sonst
    // ueber decideOnOutdated vermeidet.
    useEffect(() => {
        if (!enabled || state.phase !== 'locked' || adminEntered) return;
        const timer = setInterval(() => {
            if (typeof document !== 'undefined' && document.hidden) return;
            void argsRef.current.checkVersionNow().catch(() => undefined);
        }, LOCKED_VERSION_POLL_MS);
        return () => clearInterval(timer);
    }, [enabled, state.phase, adminEntered]);

    const enterAsAdmin = useCallback(() => {
        if (state.adminBypass) setAdminEntered(true);
    }, [state.adminBypass]);

    const endNow = useCallback(async (): Promise<void> => {
        try {
            await endMaintenance();
        } catch (e) {
            // Der Admin steht vor der Wartungsseite und muss sehen, dass sein
            // Klick nichts bewirkt hat — sonst glaubt er, alle seien wieder drin.
            console.error('[bauphase] Beenden fehlgeschlagen:', e);
            const text = e instanceof Error ? e.message : String(e);
            window.alert(`Bauphase konnte nicht beendet werden: ${text}`);
        } finally {
            await refresh();
        }
    }, [refresh]);

    const locked = enabled && state.phase === 'locked';

    // showScreen erst nach `flushDone`: die Wartungsseite ersetzt in App.tsx
    // den ganzen Baum (auch das Freelancer-Portal). Stuende sie sofort bei
    // 'locked', waere die FreelancerInspection samt ihrer angemeldeten
    // Sicherung schon abgebaut, bevor Schritt 0 sie aufruft — die
    // ungespeicherten Maengel waeren weg. Bis dahin bleibt die App sichtbar;
    // der Flush dauert Sekunden, der Server weist Saves ohnehin schon ab.
    return {
        state,
        locked,
        showScreen: locked && flushDone && !(state.adminBypass && adminEntered),
        queuedHint,
        adminEntered,
        uploadsBlocked: locked && flushDone && !state.adminBypass,
        enterAsAdmin,
        endNow,
    };
};
